import {
  Folder as FolderIcon,
  Layers,
  Pencil,
  Trash2,
  FolderPlus,
} from 'lucide-react'
import { Folder, VaultItem } from '../../config'
import { gradientStyle, trackPointer } from '../../lib/folderColors'

interface WalletProps {
  folders: (Folder & { depth: number })[]
  items: VaultItem[]
  selectedFolderId: string | null
  onSelect: (id: string | null) => void
  onCreateFolder: () => void
  onEditFolder: (id: string) => void
  onDeleteFolder: (id: string) => void
}

/**
 * Folders as a wallet of gradient cards. Cards never overlap: each is a
 * fixed-height strip in a vertical list, and only the active card grows to
 * reveal a peek of its newest items plus folder actions.
 */
export function FolderWallet({
  folders,
  items,
  selectedFolderId,
  onSelect,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
}: WalletProps) {
  const newestFirst = [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )
  const entries: { id: string | null; name: string; depth: number }[] = [
    { id: null, name: 'All items', depth: 0 },
    ...folders.map((f) => ({ id: f.id, name: f.name, depth: f.depth })),
  ]

  return (
    <div>
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="text-[13px] font-semibold text-faint">Folders</h2>
        <button
          type="button"
          className="icon-btn !h-9 !w-9 !rounded-xl"
          onClick={onCreateFolder}
          aria-label="New folder"
          title="New folder"
        >
          <FolderPlus className="h-[18px] w-[18px]" />
        </button>
      </div>

      <ul className="flex flex-col gap-2.5">
        {entries.map((entry, i) => {
          const inFolder = newestFirst.filter(
            (it) => entry.id === null || it.folderId === entry.id,
          )
          const active = selectedFolderId === entry.id
          return (
            <li
              key={entry.id ?? '__all'}
              className="animate-deal-in"
              style={{
                animationDelay: `${100 + Math.min(i, 8) * 60}ms`,
                paddingLeft: Math.min(entry.depth, 3) * 14,
              }}
            >
              <div
                className={`wcard gcard ${active ? 'is-active' : ''}`}
                style={gradientStyle(entry.id)}
                onPointerMove={trackPointer}
              >
                <button
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  onClick={() => onSelect(entry.id)}
                  className="flex min-h-[60px] w-full items-center gap-3 rounded-3xl px-4 text-left"
                >
                  {entry.id === null ? (
                    <Layers className="h-[18px] w-[18px] flex-none opacity-90" />
                  ) : (
                    <FolderIcon className="h-[18px] w-[18px] flex-none opacity-90" />
                  )}
                  <span className="min-w-0 flex-1 truncate font-display text-[15.5px] font-semibold tracking-tight">
                    {entry.name}
                  </span>
                  <span className="grid h-[26px] min-w-[26px] place-items-center rounded-[9px] bg-white/[0.18] px-2 text-[12.5px] font-bold">
                    {inFolder.length}
                  </span>
                </button>

                <div className="wcard-more" aria-hidden={!active}>
                  <div>
                    <div className="px-4 pb-4">
                      {inFolder.length > 0 ? (
                        <ul className="grid gap-1 border-t border-white/15 pt-3">
                          {inFolder.slice(0, 3).map((it) => (
                            <li
                              key={it.id}
                              className="truncate text-[13.5px] text-white/[0.82]"
                            >
                              {it.title}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="border-t border-white/15 pt-3 text-[13.5px] text-white/[0.82]">
                          Nothing here yet
                        </p>
                      )}
                      {entry.id !== null && (
                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            tabIndex={active ? 0 : -1}
                            onClick={() => onEditFolder(entry.id!)}
                            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-white/[0.14] px-3 text-[13px] font-semibold transition-colors hover:bg-white/[0.22]"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Rename
                          </button>
                          <button
                            type="button"
                            tabIndex={active ? 0 : -1}
                            onClick={() => onDeleteFolder(entry.id!)}
                            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-black/20 px-3 text-[13px] font-semibold transition-colors hover:bg-black/30"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
